/**
 * The socket's AUTH state (fix E / E6) — WEB-only, and deliberately NOT in the
 * shared core.
 *
 * `Application.tsx` owns `authComplete` and hands it to `SocketProvider`
 * (`setAuthComplete`) and to the play screen as a prop; the old screen turned
 * `!authComplete` into the `ConnectionModal`'s "Auth Failed · Reconnect" face
 * (`ConnectionModal/index.js` `getCopy`, mounted at `index.js:5777-5782`). The
 * APP lane has no such prop anywhere (`grep authComplete` in the app repo is
 * empty — its socket provider does not expose one), so putting the flag in the
 * mirrored `session` slice would add a field the app can never write. Same
 * reasoning as `rail/analysis/analysisStore.ts`: web-only surface state lives
 * in a web-only store next to the thing that reads it.
 *
 * The screen ROOT is the only writer (it is the one component that may read a
 * prop), so `Strip` stays context-free and prop-free.
 *
 * Default TRUE, exactly as `Application.tsx`'s own `useState(true)`: until the
 * socket says otherwise there is nothing wrong, and a strip that cried
 * "Authentication failed" for the first frame of every mount would be worse
 * than no strip at all.
 *
 * F10 (fix F-B, 2026-10-06) ADDS `registered` / `watchdogExhausted`: whether
 * `hooks/useGameplayHandlers.ts`'s `fetch_game_state` for THIS connection has
 * been acknowledged, and whether its watchdog gave up resending it. Written
 * there, read by `Strip/model.ts`'s fourth face — same reasoning as
 * `complete`: a web-only transport signal next to the web-only strip that
 * shows it, not in the shared `session` slice.
 *
 * Fix I (2026-10-06) ADDS `clientChanged` / `clearRemoteClientChanged`: the
 * root mirrors "opened in another tab" into `session.anotherTabActive`
 * (`index.tsx`, EITHER `props.changeClient` — the server's real
 * `change_client_connection`, fix E6's missing half — OR GamePlayStore's
 * `showChangeInClient`), and duplicates the SAME value here so
 * `overlay/ClientChanged` — the card that now owns this face and the
 * auth-failed one (`Strip` dropped both) — can derive its `{anotherTab,
 * authFailed}` from ONE store instead of reaching GamePlayStore or a prop
 * itself. `clearRemoteClientChanged` is the card's only way back INTO a
 * prop: `props.setChangeClient(false)`, wrapped by the root (only it may
 * read a prop) and refreshed in the same effect as `clientChanged`.
 */
import {create} from 'zustand';

const noop = () => {};

export type SocketAuthStore = {
  /** the server accepted the token (`auth_complete` with a user) */
  complete: boolean;
  setComplete: (complete: boolean) => void;
  /** F10: the current connection's `fetch_game_state` got an ack */
  registered: boolean;
  setRegistered: (registered: boolean) => void;
  /** F10: the registration watchdog resent 3 times with no ack */
  watchdogExhausted: boolean;
  setWatchdogExhausted: (watchdogExhausted: boolean) => void;
  /** fix I: `props.changeClient` OR GamePlayStore's `showChangeInClient` */
  clientChanged: boolean;
  setClientChanged: (clientChanged: boolean) => void;
  /** fix I: `props.setChangeClient(false)` if the root has one, else a noop */
  clearRemoteClientChanged: () => void;
  setClearRemoteClientChanged: (clearRemoteClientChanged: () => void) => void;
};

export const useSocketAuth = create<SocketAuthStore>(set => ({
  complete: true,
  setComplete: complete =>
    set(state => (state.complete === complete ? state : {complete})),
  registered: false,
  setRegistered: registered =>
    set(state => (state.registered === registered ? state : {registered})),
  watchdogExhausted: false,
  setWatchdogExhausted: watchdogExhausted =>
    set(state =>
      state.watchdogExhausted === watchdogExhausted
        ? state
        : {watchdogExhausted},
    ),
  clientChanged: false,
  setClientChanged: clientChanged =>
    set(state =>
      state.clientChanged === clientChanged ? state : {clientChanged},
    ),
  clearRemoteClientChanged: noop,
  setClearRemoteClientChanged: clearRemoteClientChanged =>
    set({clearRemoteClientChanged}),
}));

export default useSocketAuth;
